import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';

import { ChatHistoryPageResponse, ChatTurnResponse, ProductCardResponse, StorefrontChatApiService } from '../api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  products?: ProductCardResponse[];
}

/** Turns per history page, both the initial load and each scroll-up "load older" fetch. */
const HISTORY_PAGE_SIZE = 5;

/**
 * State for the storefront chat widget (RBA-5773/RBA-5774). Lives only in this signal — never
 * localStorage/sessionStorage — so a guest's history disappears on tab close/reload by
 * construction, matching the widget's "no persistence for guests" requirement even though their
 * identity cookie technically survives at the browser-process level.
 */
@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly api = inject(StorefrontChatApiService);

  private readonly openState = signal(false);
  private readonly messagesState = signal<ChatMessage[]>([]);
  private readonly sendingState = signal(false);
  private readonly errorState = signal<string | null>(null);
  private readonly errorParamsState = signal<Record<string, unknown> | null>(null);
  private readonly historyLoadingState = signal(false);
  private readonly hasMoreHistoryState = signal(false);
  private readonly loadingOlderState = signal(false);
  private conversationId: string | undefined;
  /** The oldest turn loaded so far - the cursor for the next "load older" page. */
  private oldestTurnCreatedAt: string | undefined;

  readonly open = this.openState.asReadonly();
  readonly messages = this.messagesState.asReadonly();
  readonly sending = this.sendingState.asReadonly();
  readonly error = this.errorState.asReadonly();
  readonly errorParams = this.errorParamsState.asReadonly();
  readonly hasMessages = computed(() => this.messagesState().length > 0);
  /** True only while reopening an existing conversation; lets the widget show a loading
   * skeleton instead of flashing the "no messages yet" empty state for however long the
   * reload takes. */
  readonly historyLoading = this.historyLoadingState.asReadonly();
  /** True while older turns still exist on the server - the widget shows/arms the
   * scroll-to-load-more affordance only while this is true. */
  readonly hasMoreHistory = this.hasMoreHistoryState.asReadonly();
  readonly loadingOlder = this.loadingOlderState.asReadonly();

  toggle(): void {
    this.openState.update((open) => !open);
  }

  /** Used when navigating away (e.g. a product card link) so the panel doesn't keep covering the page. */
  close(): void {
    this.openState.set(false);
  }

  send(text: string): void {
    const message = text.trim();
    if (!message || this.sendingState()) {
      return;
    }
    this.messagesState.update((messages) => [...messages, { role: 'user', text: message }]);
    this.sendingState.set(true);
    this.errorState.set(null);

    this.api
      .chat({ message, conversationId: this.conversationId })
      .pipe(
        tap((response) => {
          this.conversationId = response.conversationId;
          this.messagesState.update((messages) => [
            ...messages,
            { role: 'assistant', text: response.reply ?? '', products: response.products },
          ]);
        }),
        catchError((error: unknown) => {
          const httpError = error as {
            status?: number;
            headers?: { get(name: string): string | null };
          };
          if (httpError?.status === 429) {
            // Daily cap (~86400s) vs the 10-minute burst window (~600s) read different enough to
            // warrant a different message - "wait a bit" is misleading when the real wait is a day.
            const retryAfterSeconds = Number(httpError.headers?.get('Retry-After'));
            if (retryAfterSeconds > 3600) {
              this.errorState.set('chat.errors.rateLimitedDaily');
              this.errorParamsState.set(null);
            } else {
              this.errorState.set('chat.errors.rateLimited');
              this.errorParamsState.set({
                minutes: Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
                  ? Math.ceil(retryAfterSeconds / 60)
                  : 10,
              });
            }
          } else {
            this.errorState.set('chat.errors.generic');
            this.errorParamsState.set(null);
          }
          return of(null);
        }),
        finalize(() => this.sendingState.set(false)),
      )
      .subscribe();
  }

  /**
   * Reloads the caller's own most recent conversation. Callers must gate this on
   * `AuthService.isAuthenticated()` — guests reopening the widget should see a blank slate
   * (RBA-5774 AC3), not the prior browser-session's history.
   */
  loadHistory(): void {
    this.historyLoadingState.set(true);
    this.api
      .conversations()
      .pipe(catchError(() => of([])))
      .subscribe((summaries) => {
        const latest = summaries[0]?.conversationId;
        if (!latest) {
          this.historyLoadingState.set(false);
          return;
        }
        this.conversationId = latest;
        this.api
          .conversation(latest, undefined, undefined, HISTORY_PAGE_SIZE)
          .pipe(
            catchError(() => of({ turns: [], hasMore: false } as ChatHistoryPageResponse)),
            finalize(() => this.historyLoadingState.set(false)),
          )
          .subscribe((page) => this.applyPage(page, 'replace'));
      });
  }

  /**
   * Fetches the next page of older turns and prepends them. No-ops (returns a completed
   * Observable immediately) when there's nothing to reopen, no older page left, or a load is
   * already in flight — callers can always just subscribe without checking first.
   */
  loadOlder(): Observable<void> {
    if (!this.conversationId || !this.hasMoreHistoryState() || this.loadingOlderState()) {
      return of(undefined);
    }
    this.loadingOlderState.set(true);
    return this.api.conversation(this.conversationId, undefined, this.oldestTurnCreatedAt, HISTORY_PAGE_SIZE).pipe(
      tap((page) => this.applyPage(page, 'prepend')),
      catchError(() => of({ turns: [], hasMore: this.hasMoreHistoryState() } as ChatHistoryPageResponse)),
      finalize(() => this.loadingOlderState.set(false)),
      map(() => undefined),
    );
  }

  private applyPage(page: ChatHistoryPageResponse, mode: 'replace' | 'prepend'): void {
    const turns = page.turns ?? [];
    this.hasMoreHistoryState.set(page.hasMore ?? false);
    if (turns.length > 0) {
      this.oldestTurnCreatedAt = turns[0].createdAt;
    }
    const prepended = turns.flatMap(toMessages);
    this.messagesState.update((messages) => (mode === 'replace' ? prepended : [...prepended, ...messages]));
  }
}

function toMessages(turn: ChatTurnResponse): ChatMessage[] {
  return [
    { role: 'user', text: turn.userMessage ?? '' },
    { role: 'assistant', text: turn.assistantReply ?? '', products: turn.products },
  ];
}
